import { useFormContext, useWatch } from "react-hook-form";
import { AdminBranchedCCEmailQuestion } from "src/features/Admin/Observations/components/AdminBranchedCCEmailQuestion";
import { AdminBranchedDateQuestion } from "src/features/Admin/Observations/components/AdminBranchedDateQuestion";
import { AdminBranchedDropdownQuestion } from "src/features/Admin/Observations/components/AdminBranchedDropdownQuestion";
import { AdminBranchedEmployeeSelectionQuestion } from "src/features/Admin/Observations/components/AdminBranchedEmployeeSelectionQuestion";
import { AdminBranchedImageUploadQuestion } from "src/features/Admin/Observations/components/AdminBranchedImageUploadQuestion";
import { AdminBranchedObservationDateQuestion } from "src/features/Admin/Observations/components/AdminBranchedObservationDateQuestion";
import { AdminBranchedRadioQuestion } from "src/features/Admin/Observations/components/AdminBranchedRadioQuestion";
import { AdminBranchedTextQuestion } from "src/features/Admin/Observations/components/AdminBranchedTextQuestion";
import { useCreateBranchedQuestion } from "src/features/Admin/Observations/hooks/useCreateBranchedQuestion";
import {
	Dialog,
	DialogContent,
	DialogClose,
	DialogTitle,
	DialogDescription
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { X } from "lucide-react";
import { getTargetQuestionDetails } from "src/features/Admin/Observations/utils/branchedQuestions";

type BranchedQuestionsPreviewModalProps = {
	questions?: ObservationQuestion[];
	isOpen: boolean;
	onClose: () => void;
};

const BranchedQuestionsPreviewModal = ({
	isOpen,
	onClose
}: BranchedQuestionsPreviewModalProps) => {
	const { control, setValue } = useFormContext<AdminObservationForm>();

	const observationFormId = useWatch({ control, name: "observationFormId" });
	const questions = useWatch({ control, name: "questions" }) ?? [];

	const { mutate: createBranchedQuestion } =
		useCreateBranchedQuestion(observationFormId);

	const handleGoToChange = (
		value: string,
		questionId: number | null,
		answerId?: number | null
	) => {
		const targetQuestionDetails = getTargetQuestionDetails(value);

		const question = questions.find((q) => q.id === questionId);
		if (!question) return;

		const questionIndex = questions.findIndex((q) => q.id === questionId);
		if (questionIndex === -1) return;

		if (question.type === "RADIO" || question.type === "SELECT") {
			const answers = questions[questionIndex].answers;
			if (!answers) return;

			const answer = question.answers?.find((a) => a.id === answerId);
			if (!answer) return;

			answers.forEach((a) => {
				if (a.id === answerId) {
					if (targetQuestionDetails.targetQuestionId) {
						a.go_to = targetQuestionDetails.targetQuestionId;
					} else {
						a.go_to = targetQuestionDetails.targetQuestionType as any;
					}
				}
			});
			setValue(`questions.${questionIndex}.answers`, answers);
		} else {
			const goToValue =
				targetQuestionDetails.targetQuestionType === "MULTIPLE"
					? targetQuestionDetails.targetQuestionId
					: (targetQuestionDetails.targetQuestionType as any);

			setValue(`questions.${questionIndex}.goTo`, goToValue);
		}

		createBranchedQuestion({
			answer: answerId ?? null,
			question: questionId,
			targetQuestion: targetQuestionDetails.targetQuestionId,
			targetQuestionType: targetQuestionDetails.targetQuestionType
		});
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="mt-[200px] p-0 w-[80%] max-w-[900px] rounded-xl [&>button:last-child]:hidden">
				<div className="flex justify-end px-3 pt-3">
					<DialogClose className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md border border-border transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
						<X className="h-4 w-4 text-foreground" />
						<span className="sr-only">Close</span>
					</DialogClose>
				</div>
				<div className="px-6 pb-6">
					<DialogTitle className="font-heading text-lg font-medium text-foreground mb-4">
						Branching options
					</DialogTitle>
					<DialogDescription className="sr-only">
						Configure branching rules for each question in the form
					</DialogDescription>

					<div className="flex flex-col gap-6">
						{questions.map((question) => (
							<div className="w-full" key={question.id}>
								{question.type === "TEXT" && (
									<AdminBranchedTextQuestion
										question={question.question}
										note={question.note}
										questions={questions}
										goToValue={question.goTo?.toString() ?? "NEXT"}
										onGoToChange={(value) =>
											handleGoToChange(value, question.id)
										}
									/>
								)}

								{question.type === "RADIO" && (
									<AdminBranchedRadioQuestion
										question={question.question}
										note={question.note}
										options={question.answers}
										questions={questions}
										onGoToChange={(value, answerId) =>
											handleGoToChange(value, question.id, answerId)
										}
									/>
								)}

								{question.type === "SELECT" && (
									<AdminBranchedDropdownQuestion
										question={question.question}
										note={question.note}
										options={question.answers}
										questions={questions}
										onGoToChange={(value, answerId) =>
											handleGoToChange(value, question.id, answerId)
										}
									/>
								)}

								{question.type === "DATE" && (
									<AdminBranchedDateQuestion
										question={question.question}
										questions={questions}
										goToValue={question.goTo?.toString() ?? "NEXT"}
										onGoToChange={(value) =>
											handleGoToChange(value, question.id)
										}
									/>
								)}

								{question.type === "OBSERVATION_DATE" && (
									<AdminBranchedObservationDateQuestion
										question={question.question}
										questions={questions}
										goToValue={question.goTo?.toString() ?? "NEXT"}
										onGoToChange={(value) =>
											handleGoToChange(value, question.id)
										}
									/>
								)}

								{question.type === "CC_EMAIL_RECIPIENT" && (
									<AdminBranchedCCEmailQuestion
										question={question.question}
										note={question.note}
										goToValue={question.goTo?.toString() ?? "NEXT"}
										onGoToChange={(value) =>
											handleGoToChange(value, question.id)
										}
									/>
								)}

								{question.type === "IMAGE_UPLOAD" && (
									<AdminBranchedImageUploadQuestion
										question={question.question}
										questions={questions}
										note={question.note}
										goToValue={question.goTo?.toString() ?? "NEXT"}
										onGoToChange={(value) =>
											handleGoToChange(value, question.id)
										}
									/>
								)}

								{question.type === "EMPLOYEE_SELECTION" && (
									<AdminBranchedEmployeeSelectionQuestion
										question={question.question}
										questions={questions}
										note={question.note}
										goToValue={question.goTo?.toString() ?? "NEXT"}
										onGoToChange={(value) =>
											handleGoToChange(value, question.id)
										}
									/>
								)}
							</div>
						))}
					</div>

					<div className="flex justify-end mt-8">
						<Button variant="outline" size="lg" onClick={() => onClose()}>
							Back to form
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default BranchedQuestionsPreviewModal;
